import React, { ComponentProps } from "react";
import * as combobox from "@zag-js/combobox";
import { useMachine, normalizeProps, Portal } from "@zag-js/react";
import { memo, useId, useState } from "react";
import { Form, FormControlProps, InputGroup, Stack } from "react-bootstrap";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import "./Combobox.scss";

export interface ComboboxOption {
  code: string;
  name: any;
  [key: string]: any;
}

export type SelectedComboboxOption<T = any> = combobox.ValueChangeDetails<
  T & ComboboxOption
>;

interface ComboboxProps extends ComponentProps<"div"> {
  options: ComboboxOption[];
  inputLabel?: string;
  inputProps?: Omit<FormControlProps, "children">;
  handleSelection: (newValue: SelectedComboboxOption) => void;
}

function Combobox({
  options,
  inputLabel,
  handleSelection,
  inputProps,
  ...props
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [comboboxOptions, setComboboxOptions] = useState(options);
  const collection = combobox.collection({
    items: options,
    itemToValue: (item) => item.code,
    itemToString: (item) => item.name,
  });

  const [state, send] = useMachine(
    combobox.machine({
      id: useId(),
      collection,
      openOnClick: true,
      onOpenChange({ open }) {
        setIsOpen(open);
        // The spread syntax was used on the right hand side to convert
        // state which is a ProxyObject to a normal object
        const { collection: internalCollectionData } = { ...state.context };
        setComboboxOptions(internalCollectionData.items);
      },
      onInputValueChange({ inputValue }) {
        const { collection: internalCollectionData } = { ...state.context };
        const filtered = internalCollectionData.items.filter((item) =>
          item.name.toLowerCase().includes(inputValue.toLowerCase())
        );
        setComboboxOptions(
          filtered.length > 0 ? filtered : internalCollectionData.items
        );
      },
      onValueChange(details) {
        handleSelection(details);
      },
    }),
    {
      context: { collection },
    }
  );

  const api = combobox.connect(state, send, normalizeProps);
  const { size, value: inputValue, ...defaultInputProps } = api.getInputProps();
  const { children, ...defaultInputPropsWithoutChildren } = defaultInputProps;

  return (
    <div {...props} className="Combobox">
      <Form {...api.getRootProps()}>
        {inputLabel && (
          <label
            {...api.getLabelProps()}
            className="mb-2 Grotesk SpecialFontSettings InputLabel"
          >
            {inputLabel}
          </label>
        )}
        <div {...api.getControlProps()}>
          <InputGroup>
            <Form.Control
              {...defaultInputPropsWithoutChildren}
              value={inputValue?.toString()}
              className="Input"
              {...inputProps}
            />
            <InputGroup.Text
              aria-label="Show dropdown"
              {...api.getTriggerProps()}
              className="InputAddon"
            >
              <ChevronDownIcon fill="#262626" width={10} height={11} />
            </InputGroup.Text>
          </InputGroup>
        </div>
      </Form>

      {isOpen && (
        <Portal>
          <div {...api.getPositionerProps()} className="Dropdown">
            {comboboxOptions.length > 0 && (
              <Stack {...api.getContentProps()} className="DropdownList">
                {comboboxOptions.map((item) => (
                  <li
                    key={item.code}
                    {...api.getItemProps({ item })}
                    className="Grotesk SpecialFontSettings DropdownItem"
                  >
                    {item.name}
                  </li>
                ))}
              </Stack>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}

export default memo(Combobox);
